import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Eye, EyeOff, GraduationCap, Lock, Mail, MailCheck, UserRound, Users } from "lucide-react";
import { AuthShowcase } from "@/components/AuthShowcase";
import { BrandLogo } from "@/components/BrandLogo";
import { Field, Tile, inputCls } from "@/components/forms/FormKit";

export default function Auth({ mode = "login" }) {
  const { login, register, verifyOtp, resendOtp, user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/dashboard";

  const [form, setForm] = useState({ email: "", password: "", name: "", role: "student" });
  const [busy, setBusy] = useState(false);

  const [step, setStep] = useState("form");
  const [pendingEmail, setPendingEmail] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { if (user) nav(nextUrl); }, [user]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  useEffect(() => {
    const verifyEmail = params.get("verify");
    if (!verifyEmail) { return; }
    goToOtp(verifyEmail);
    resendOtp(verifyEmail)
      .then(() => toast.success("Fresh code sent", { description: "Check your inbox for a new 6-digit code." }))
      .catch((err) => {
        if (err.response?.status !== 429) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
      });
  }, []);

  const startCooldown = () => {
    setCooldown(60);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const goToOtp = (email) => {
    setPendingEmail(email);
    setStep("otp");
    setCode("");
    startCooldown();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
        nav(nextUrl);
      } else {
        await register(form);
        goToOtp(form.email);
        toast.success("Check your inbox", { description: "We sent a 6-digit verification code to your email." });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (mode === "login" && status === 403 && detail === "email_not_verified") {
        goToOtp(form.email);
        toast.message("Verify your email", { description: "We sent a 6-digit code to your inbox to finish signing in." });
      } else if (detail === "undeliverable_email") {
        toast.error("That email looks undeliverable. Please use a real email you can access.");
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyOtp(pendingEmail, code.trim());
      toast.success("Email verified");
      nav(nextUrl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await resendOtp(pendingEmail);
      startCooldown();
      toast.success("New code sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + nextUrl;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white grid md:grid-cols-2 relative overflow-hidden">
      <AuthShowcase />
      <div className="relative flex items-center justify-center px-6 py-10 md:py-12">
        <div aria-hidden className="absolute top-0 right-0 w-[26rem] h-[26rem] rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="relative w-full max-w-md">
          <div className="md:hidden mb-8"><BrandLogo testid="auth-brand-logo-mobile" /></div>
          {step === "otp" ? (
            <OtpStep email={pendingEmail} code={code} setCode={setCode} busy={busy} cooldown={cooldown} onSubmit={verify} onResend={resend} onBack={() => setStep("form")} />
          ) : (
            <CredentialsForm mode={mode} form={form} setForm={setForm} busy={busy} onSubmit={submit} onGoogle={google} />
          )}
        </div>
      </div>
    </div>
  );
}

function useNextQuery() {
  const [params] = useSearchParams();
  const next = params.get("next");
  return next ? `?next=${encodeURIComponent(next)}` : "";
}

function ModeSwitch({ mode }) {
  const q = useNextQuery();
  const tab = (m, label) => (
    <Link to={`/${m}${q}`} data-testid={`auth-tab-${m}`} className={`flex-1 rounded-full py-2 text-center text-sm font-medium transition-[background-color,color] duration-200 ${mode === m ? "bg-amber-500 text-black" : "text-zinc-400 hover:text-white"}`}>{label}</Link>
  );
  return <div className="mt-6 flex rounded-full border border-zinc-800 bg-zinc-950/70 p-1">{tab("login", "Sign in")}{tab("signup", "Create account")}</div>;
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
  );
}

function RolePicker({ role, onPick }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-400">I am a…</div>
      <div className="mt-1.5 grid grid-cols-2 gap-2.5">
        <Tile testid="role-student" on={role === "student"} onClick={() => onPick("student")} icon={GraduationCap} title="Student (adult)" sub="Learning for myself" />
        <Tile testid="role-parent" on={role === "parent"} onClick={() => onPick("parent")} icon={Users} title="Parent / Guardian" sub="Booking for my child" />
      </div>
    </div>
  );
}

function CredentialsForm({ mode, form, setForm, busy, onSubmit, onGoogle }) {
  const [showPw, setShowPw] = useState(false);
  const q = useNextQuery();
  const isLogin = mode === "login";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={onSubmit} data-testid={`auth-form-${mode}`} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
      <Link to="/" data-testid="auth-back-home" className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400 transition-colors">
        <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Back to home
      </Link>
      <ModeSwitch mode={mode} />
      <div className="mt-8 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">{isLogin ? "Welcome back" : "Create account"}</div>
      <h1 className="font-adult-heading text-3xl sm:text-4xl font-semibold tracking-tight">
        {isLogin ? <>Sign in to <span className="font-adult-serif italic text-amber-400">continue</span></> : <>Get started in <span className="font-adult-serif italic text-amber-400">20 seconds</span></>}
      </h1>
      <div className="mt-8 space-y-4">
        {!isLogin && (
          <>
            <Field label="Full name" htmlFor="name" required icon={UserRound} filled={form.name.trim().length > 1}>
              <input data-testid="auth-name" id="name" required autoComplete="name" value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </Field>
            <RolePicker role={form.role} onPick={(role) => setForm({ ...form, role })} />
          </>
        )}
        <Field label="Email" htmlFor="email" required icon={Mail} filled={/\S+@\S+\.\S+/.test(form.email)}>
          <input data-testid="auth-email" id="email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
        </Field>
        <Field label="Password" htmlFor="password" required icon={Lock}>
          <input data-testid="auth-password" id="password" type={showPw ? "text" : "password"} required minLength={6} autoComplete={isLogin ? "current-password" : "new-password"} value={form.password} onChange={set("password")} placeholder={isLogin ? "Your password" : "At least 6 characters"} className={inputCls} />
          <button type="button" data-testid="auth-password-toggle" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-zinc-500 hover:text-amber-400 transition-colors">
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </Field>
        {isLogin && (
          <div className="text-right -mt-1">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-amber-400 hover:text-amber-300">Forgot password?</Link>
          </div>
        )}
      </div>
      <Button type="submit" data-testid="auth-submit" disabled={busy} className="group w-full mt-6 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full py-6 text-base glow-amber">
        {busy ? "Please wait…" : <>{isLogin ? "Sign in" : "Create account"}<ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" /></>}
      </Button>
      <div className="my-6 flex items-center gap-3 text-xs text-zinc-600 font-adult-mono uppercase tracking-[0.2em]">
        <div className="h-px bg-zinc-800 flex-1" /> or <div className="h-px bg-zinc-800 flex-1" />
      </div>
      <button type="button" data-testid="google-signin-btn" onClick={onGoogle} className="w-full h-12 rounded-full border border-zinc-800 bg-zinc-950/60 hover:border-zinc-600 hover:bg-zinc-900 flex items-center justify-center gap-2 text-sm transition-smooth">
        <GoogleIcon /> Continue with Google
      </button>
      <div className="mt-6 text-sm text-zinc-400 text-center">
        {isLogin ? (
          <>New here? <Link to={`/signup${q}`} className="text-amber-400 hover:text-amber-300">Create account</Link></>
        ) : (
          <>Already have an account? <Link to={`/login${q}`} className="text-amber-400 hover:text-amber-300">Sign in</Link></>
        )}
      </div>
    </form>
  );
}

function OtpStep({ email, code, setCode, busy, cooldown, onSubmit, onResend, onBack }) {
  return (
    <form onSubmit={onSubmit} data-testid="auth-otp-step" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-5 glow-amber">
        <MailCheck className="w-6 h-6" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">Verify email</div>
      <h1 className="font-adult-heading text-3xl font-semibold tracking-tight">Enter your code</h1>
      <p className="mt-3 text-sm text-zinc-400">We sent a 6-digit code to <span className="text-zinc-200">{email}</span>. It expires in 10 minutes.</p>
      <div className="mt-8">
        <Label htmlFor="otp" className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-400">Verification code</Label>
        <Input data-testid="auth-otp-input" id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456"
          className="mt-2 bg-zinc-950 border-zinc-800 h-14 text-center text-2xl tracking-[0.5em] font-adult-mono focus-visible:ring-amber-500/40" />
      </div>
      <Button type="submit" data-testid="auth-otp-submit" disabled={busy || code.length < 6} className="w-full mt-6 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full py-6 text-base glow-amber disabled:opacity-50">
        {busy ? "Verifying…" : "Verify & continue"}
      </Button>
      <div className="mt-6 flex items-center justify-between text-sm">
        <button type="button" data-testid="auth-otp-back" onClick={onBack} className="text-zinc-500 hover:text-zinc-300">← Back</button>
        <button type="button" data-testid="auth-otp-resend" onClick={onResend} disabled={cooldown > 0} className="text-amber-400 hover:text-amber-300 disabled:text-zinc-600 disabled:cursor-not-allowed">
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}
